import { Box, Divider } from "@mui/material";
import BookmarkPlan from "components/common/Bookmark";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import BasicTable from "components/common/Table";
import { useTranslate } from "hooks/useTranslate";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import {
  PAGE_SIZE,
  abovePropertyViewTypePlan,
  brandDeadlinesType,
  initiativePlansType,
  noAbovePropertyPlans,
  tabConstants
} from "utils/CommonConstants";
import {
  formFilterRequestPayload,
  functionalAreaIdToName,
  functionalAreaIdsToNames,
  getHltInncodes,
  getMoreValues,
  isAboveUserPlanBookmarked
} from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import { routes } from "utils/routes";
import { fetchInitiativeFilterAsync, fetchMultiTargetDetails } from "../AboveProperty.slice";
import { headers as BrandDeadlineHeader } from "./BrandDeadline/TableHeadersConfig";
import { headers as BrandInitiativeHeader } from "./BrandInitiatives/TableHeadersConfig";

interface Props {
  type: string;
  isInitiativeFilterVisible?: boolean;
}

const BrandPlansList = (props: Props) => {
  const translate = useTranslate();
  const { type, isInitiativeFilterVisible = false } = props;
  const [pageNo, setPageNo] = useState(1);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const plans = useAppSelector((state) => state.aboveProperty.multiTargetDetails);
  const totalRecords = useAppSelector((state) => state.aboveProperty.multiTargetTotalRecords);
  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);
  const { initiativeFilterValues } = useAppSelector((state) => state.aboveProperty);
  const pageName =
    type === "initiativeFilters" ? filterPage?.plansPageInitiativesDisplay : filterPage.plansPageBrandDeadlineDisplay;
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[pageName]);
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload);
  const searchText = globalFiltersforPage.searchText;

  useEffect(() => {
    dispatch(
      fetchMultiTargetDetails({
        innCodes: getHltInncodes(),
        filter: {
          ...formattedFilterRequestPayload
        },
        sort: null,
        pagination: {
          page: pageNo,
          size: PAGE_SIZE
        },
        searchText: searchText,
        planType: [type === translate("dashboard.PlanView.BrandDeadline") ? brandDeadlinesType : initiativePlansType],
        isSortingDisabled: true
      })
    );
    if (initiativeFilterValues.length <= 0 && type === "initiativeFilters")
      dispatch(fetchInitiativeFilterAsync(getHltInncodes(true)));
  }, [pageNo, filterRequestPayload, searchText]);

  useEffect(() => {
    setPageNo(1);
  }, [filterRequestPayload, searchText]);

  return (
    <Box sx={{ backgroundColor: tokens.Background.General.Primary, borderRadius: "12px", overflow: "hidden" }}>
      <Box sx={{ padding: "16px 8px" }}>
        {" "}
        <GlobalFilters isSearchBarVisible page={pageName} isPreDefinedNav={true} searchedText={searchText} />
        <GlobalFilterDescription page={pageName} />
      </Box>

      <Divider />
      <BasicTable
        data={{
          headers: type === translate("dashboard.PlanView.BrandDeadline") ? BrandDeadlineHeader : BrandInitiativeHeader,
          rows: plans.map((plan: any) => {
            return {
              onClick: () => {},
              data: [
                {
                  content: (
                    <Box
                      onClick={() =>
                        navigate(
                          `${routes.PlanProgress}/${plan?.id}?redirectFrom=${
                            type === translate("dashboard.PlanView.BrandDeadline")
                              ? tabConstants.BrandDeadline
                              : tabConstants.BrandInitiative
                          }`,
                          {
                            state: { progressInncodes: plan?.properties }
                          }
                        )
                      }
                      sx={{
                        textDecoration: "none",
                        cursor: "pointer",
                        color: tokens.Text.General.PrimaryHeadline,
                        fontWeight: "700"
                      }}
                    >
                      {plan?.name}
                    </Box>
                  ),
                  showBold: true
                },
                {
                  content: getMoreValues(functionalAreaIdsToNames(plan.functionalAreaIds)),
                  tooltip: {
                    title: translate("dashboard.PlanView.FunctionalArea"),
                    list: plan.functionalAreaIds?.map((FA: any, index: number) => {
                      return { name: functionalAreaIdToName(FA, functionalAreaList) };
                    })
                  }
                },
                { content: plan.propertiesCount },
                type === translate("dashboard.PlanView.BrandDeadline")
                  ? {
                      content: getUIFormatDate(plan.brandDeadlines?.[0]?.["deadline"]),
                      color: moment(plan.brandDeadlines?.[0]?.["deadline"]).isBefore(new Date())
                        ? tokens.Text.Feedback.Alert
                        : ""
                    }
                  : { content: plan.initiativeName },
                {
                  content: (
                    <BookmarkPlan
                      aboveUserPayload={{
                        isAboveUsertype: true,
                        viewType: abovePropertyViewTypePlan
                      }}
                      cmsId={plan?.id}
                      isBookmarked={isAboveUserPlanBookmarked(plan?.id, "bookmarkedIdList")}
                    />
                  )
                }
              ]
            };
          })
        }}
        totalRecords={totalRecords}
        noDataMessage={noAbovePropertyPlans}
        pagination={{ pageNo, setPageNo }}
      />
    </Box>
  );
};

export default BrandPlansList;
